/**
 * @file docStorageService.ts
 * @description
 * This service handles all document storage interactions with Supabase:
 * 1) Uploading **user-uploaded** documents to the 'user-uploaded-docs' bucket
 *    and creating corresponding entries in the `documents` table with `user_id` set to the uploader.
 * 2) **NEW**: Uploading **system-generated** documents (like PDF or Markdown from final submission data)
 *    to the 'tempus-created-docs' bucket. For these entries, `user_id = null` to indicate they were
 *    generated by the system rather than a user.
 *
 * The service also offers functions to:
 *  - Fetch documents by submission + fieldName
 *  - Delete documents (removes from bucket and DB)
 *  - Retrieve public URLs
 *
 * Key responsibilities:
 * - Maintain separation between user documents vs. system-generated docs through separate buckets.
 * - Provide robust error handling and logging at each step.
 * - Clean up partially uploaded files if a subsequent DB operation fails.
 *
 * @dependencies
 * - Supabase JS client
 * - UUID library for unique IDs
 *
 * @notes
 * - The code uses two buckets: 'user-uploaded-docs' for user-driven uploads,
 *   and 'tempus-created-docs' for system-generated PDF/MD documents.
 * - Inserted rows in `documents` differ by user_id:
 *     - user-uploaded docs: user_id = actual user
 *     - system-generated docs: user_id = null
 * - The new function `uploadSystemGeneratedDocument` implements the plan from "Step 2" in the docs.
 */

import { getSupabaseClient } from '../lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';

/**
 * We re-use a shared or local Supabase client. For demonstration, we re-initialize here.
 * In a real codebase, you might import from a central supabaseClient.ts.
 */
const supabase = getSupabaseClient();

/** 
 * Bucket for user-driven uploads 
 */
const USER_BUCKET = 'user-uploaded-docs';

/** 
 * Bucket for system-generated PDF/Markdown 
 */
const SYSTEM_GENERATED_BUCKET = 'tempus-created-docs';

/**
 * Helper function for structured logging across all docStorageService operations.
 */
function logOperation(operation: string, details: Record<string, any>, error?: any) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && { 
      error: typeof error === 'object'
        ? {
            message: error.message,
            name: error.name,
            code: (error as any).code,
            stack: error.stack,
          }
        : error
    })
  };
  
  if (error) {
    console.error(`STORAGE_LOG [${operation}]:`, logObj);
  } else {
    console.log(`STORAGE_LOG [${operation}]:`, logObj);
  }
  return logObj;
}

/**
 * DocumentMetadata
 * ----------------
 * Represents a row in the `documents` table. 
 * 
 * @property id - Unique ID for this document record (UUID)
 * @property submission_id - The submission to which this doc belongs
 * @property user_id - The user who uploaded it (or null if system-generated)
 * @property filename - The file's original or system-constructed name
 * @property file_path - The path to the file in the relevant storage bucket
 * @property uploaded_at - Timestamp of when the doc was inserted
 * @property type - A short string denoting doc type (e.g. 'doc_protocol', 'submission_form_pdf', etc.)
 * @property is_active - Whether the doc is considered active. Optional (may be undefined if not returned).
 */
export interface DocumentMetadata {
  id: string;
  submission_id: string;
  user_id: string | null;
  filename: string;
  file_path: string;
  uploaded_at: string;
  type: string;
  is_active?: boolean; 
}

/**
 * ErrorResponse
 * -------------
 * If an operation fails, we return an object with a message and optional code
 */
export interface ErrorResponse {
  message: string;
  code?: string;
}

/**
 * For multiple-doc uploads, we can return either an error or a success
 * containing the newly created DocumentMetadata array.
 */
interface UploadSuccessResponse {
  documents: DocumentMetadata[];
}
export type UploadResponse = UploadSuccessResponse | ErrorResponse;

/**
 * isErrorResponse
 * ---------------
 * Helper to check if a given object is an ErrorResponse vs. a doc array
 */
export function isErrorResponse(
  response: DocumentMetadata | ErrorResponse | UploadResponse
): response is ErrorResponse {
  return 'message' in response && !('documents' in response);
}

/* -------------------------------------------------------------------------
    USER-UPLOADED DOCUMENTS SECTION
   -------------------------------------------------------------------------*/

/**
 * uploadSingleDocument
 * --------------------
 * Uploads a single file to the user-driven bucket 'user-uploaded-docs'.
 * Creates a record in `documents` with user_id set to the uploading user.
 * 
 * @param file         The browser File object
 * @param submissionId The submission to associate with
 * @param userId       The uploading user's ID
 * @param fieldName    The form field name, used to set `type` in the DB
 * @returns DocumentMetadata or ErrorResponse
 */
export async function uploadSingleDocument(
  file: File,
  submissionId: string,
  userId: string,
  fieldName: string
): Promise<DocumentMetadata | ErrorResponse> {
  logOperation('uploadSingleDocument:start', {
    filename: file.name,
    fileSize: file.size,
    fileType: file.type,
    submissionId,
    userId,
    fieldName
  });

  try {
    // Generate a unique path
    const filename = file.name;
    const fileExt = filename.split('.').pop();
    const fileId = uuidv4();
    const filePath = `${submissionId}/${fileId}.${fileExt}`;
    
    logOperation('uploadSingleDocument:storage-upload', { filePath, bucket: USER_BUCKET });

    // Upload the file
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(USER_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });
    if (uploadError) {
      logOperation('uploadSingleDocument:storage-upload-error', { filePath }, uploadError);
      if (uploadError.message.includes('size')) {
        return { message: 'File size exceeds the maximum allowed limit.' };
      }
      return { message: uploadError.message };
    }

    logOperation('uploadSingleDocument:storage-upload-success', {
      filePath,
      uploadData
    });

    // Create a doc record in the DB
    const documentId = uuidv4();
    const now = new Date().toISOString();

    const documentMetadata: DocumentMetadata = {
      id: documentId,
      submission_id: submissionId,
      user_id: userId,  // user-driven
      filename: filename,
      file_path: filePath,
      uploaded_at: now,
      type: fieldName,
      is_active: true,
    };

    logOperation('uploadSingleDocument:db-insert', { documentId, filePath });

    const { error: insertError } = await supabase
      .from('documents')
      .insert([documentMetadata]);

    if (insertError) {
      logOperation('uploadSingleDocument:db-insert-error', { documentId, filePath }, insertError);
      // Remove the file from bucket if DB insertion fails
      await supabase.storage.from(USER_BUCKET).remove([filePath]);
      return { message: 'Failed to save document metadata.' };
    }

    logOperation('uploadSingleDocument:success', {
      documentId,
      submissionId,
      filePath
    });

    return documentMetadata;
  } catch (error) {
    logOperation('uploadSingleDocument:unexpected-error', {}, error);
    return { message: 'An unexpected error occurred during upload.' };
  }
}

/**
 * uploadMultipleDocuments
 * -----------------------
 * For user-driven multiple-file upload. 
 * 
 * @param files        Array of File objects
 * @param submissionId Submission ID
 * @param userId       The uploading user's ID
 * @param fieldName    The form field name used to set `type`
 * @returns UploadResponse (either { documents: DocumentMetadata[] } or { message: string })
 */
export async function uploadMultipleDocuments(
  files: File[],
  submissionId: string,
  userId: string,
  fieldName: string
): Promise<UploadResponse> {
  try {
    const uploadPromises = Array.from(files).map((file) =>
      uploadSingleDocument(file, submissionId, userId, fieldName)
    );
    const results = await Promise.all(uploadPromises);

    // If any fail, we surface the first error
    const errors = results.filter((res): res is ErrorResponse => isErrorResponse(res));
    if (errors.length > 0) {
      return errors[0];
    }

    // All succeeded
    return {
      documents: results as DocumentMetadata[],
    };
  } catch (error) {
    logOperation('uploadMultipleDocuments:unexpected-error', {}, error);
    return { message: 'An unexpected error occurred during batch upload.' };
  }
}

/* -------------------------------------------------------------------------
    SYSTEM-GENERATED DOCUMENTS SECTION (PDF / MARKDOWN)
   -------------------------------------------------------------------------*/

/**
 * SystemDocType
 * -------------
 * Enumerates the possible system doc types we store in 'tempus-created-docs'.
 * e.g. `'submission_form_pdf'`, `'submission_form_markdown'`
 */
export type SystemDocType = 'submission_form_pdf' | 'submission_form_markdown';

/**
 * buildSystemGeneratedFilename
 * ----------------------------
 * Constructs a sanitized file name of the form:
 *   refName_sponsorName_protocolNo_YYYY-MM-DD_HHMMss.ext
 * with spaces / special chars replaced by hyphens.
 *
 * @param refName        e.g. "MyStudy"
 * @param sponsorName    e.g. "ACME Inc."
 * @param protocolNumber e.g. "ACME-123"
 * @param extension      e.g. "pdf"
 * @returns A sanitized string suitable for the file name
 */
function buildSystemGeneratedFilename(
  refName: string,
  sponsorName: string,
  extension: string
): string {
  // We'll define a helper to sanitize by replacing non-alphanumeric with hyphens
  // (including spaces, punctuation, etc.)
  const sanitize = (input: string) =>
    input.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/--+/g, '-');

  const dateObj = new Date();
  const datePart = dateObj.toISOString().split('T')[0]; // "YYYY-MM-DD"
  const timePart = dateObj
    .toTimeString()
    .split(' ')[0]
    .replace(/:/g, ''); // "HHMMSS"

  const safeRef = sanitize(refName);
  const safeSponsor = sanitize(sponsorName);

  // e.g. "MyStudy_ACME-Inc_2025-03-14_092012.pdf"
  return `${safeRef}_${safeSponsor}_${datePart}_${timePart}.${extension}`;
}

/**
 * uploadSystemGeneratedDocument
 * -----------------------------
 * Uploads a system-generated Blob (PDF or Markdown) to 'tempus-created-docs',
 * and inserts a row in `documents` with user_id = null, is_active = true.
 *
 * @param args An object containing:
 *   - submissionId: the associated submission's ID
 *   - fileBlob: the binary data representing the PDF or MD file
 *   - docType: e.g. 'submission_form_pdf' or 'submission_form_markdown'
 *   - refName, sponsorName, sponsorProtocolNumber: used for naming
 * @returns A DocumentMetadata or ErrorResponse
 *
 * @example
 * ```
 * await uploadSystemGeneratedDocument({
 *   submissionId: '123e4567-e89b-12d3-a456-426614174000',
 *   fileBlob: myPdfBlob,
 *   docType: 'submission_form_pdf',
 *   refName: 'MyStudy',
 *   sponsorName: 'ACME Inc.',
 *   sponsorProtocolNumber: 'ACME-123'
 * });
 * ```
 *
 * This will produce a filename like `MyStudy_ACME-Inc_ACME-123_2025-03-14_092012.pdf` 
 * and store it in 'tempus-created-docs/{submissionId}/{that-filename}'.
 */
export async function uploadSystemGeneratedDocument(args: {
  submissionId: string;
  fileBlob: Blob;
  docType: SystemDocType;
  refName: string;
  sponsorName: string;
  sponsorProtocolNumber: string;
}): Promise<DocumentMetadata | ErrorResponse> {
  const {
    submissionId,
    fileBlob,
    docType,
    refName,
    sponsorName,
    sponsorProtocolNumber,
  } = args;

  // Determine extension from docType
  // For a PDF Blob => "pdf", for markdown => "md"
  let extension = 'pdf';
  if (docType === 'submission_form_markdown') {
    extension = 'md';
  }

  logOperation('uploadSystemGeneratedDocument:start', {
    submissionId,
    docType,
    extension,
  });

  // Build a user-friendly file name
  const finalFilename = buildSystemGeneratedFilename(
    refName,
    sponsorName,
    extension
  );

  // The storage path includes the submissionId folder
  const filePath = `${submissionId}/${finalFilename}`;

  try {
    // 1) Upload to 'tempus-created-docs'
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from(SYSTEM_GENERATED_BUCKET)
      .upload(filePath, fileBlob, {
        cacheControl: '3600',
        upsert: false,
        contentType: docType === 'submission_form_pdf' 
          ? 'application/pdf'
          : 'text/markdown',
      });

    if (uploadError) {
      logOperation('uploadSystemGeneratedDocument:storage-upload-error', { filePath }, uploadError);
      // Possibly file size or other issues
      if (uploadError.message.includes('size')) {
        return { message: 'File size exceeds the maximum allowed limit.' };
      }
      return { message: uploadError.message };
    }

    logOperation('uploadSystemGeneratedDocument:storage-upload-success', {
      filePath,
      uploadData,
    });

    // 2) Insert row in `documents`
    const documentId = uuidv4();
    const now = new Date().toISOString();

    const docMeta: DocumentMetadata = {
      id: documentId,
      submission_id: submissionId,
      user_id: null, // system-generated => no user
      filename: finalFilename,
      file_path: filePath,
      uploaded_at: now,
      type: docType,
      is_active: true,
    };

    logOperation('uploadSystemGeneratedDocument:db-insert', {
      documentId,
      filePath,
      docType,
    });

    const { error: dbError } = await supabase
      .from('documents')
      .insert([docMeta]);

    if (dbError) {
      logOperation('uploadSystemGeneratedDocument:db-insert-error', { documentId, filePath }, dbError);
      // Clean up the orphaned file
      await supabase.storage.from(SYSTEM_GENERATED_BUCKET).remove([filePath]);
      return { message: 'Failed to save document metadata.' };
    }

    logOperation('uploadSystemGeneratedDocument:success', {
      documentId,
      docType,
      filePath,
    });

    // Return the newly created doc row
    return docMeta;
  } catch (error) {
    // If something else unexpected happened, log and return an error
    logOperation('uploadSystemGeneratedDocument:unexpected-error', { filePath }, error);
    return { message: 'An unexpected error occurred during system doc upload.' };
  }
}

/* -------------------------------------------------------------------------
    COMMON / SHARED METHODS
   -------------------------------------------------------------------------*/

/**
 * fetchDocuments
 * --------------
 * Retrieve all active documents in the `documents` table for a given submission and type.
 * 
 * @param submissionId The submission ID
 * @param fieldName    The doc type or fieldName
 * @returns An array of DocumentMetadata
 */
export async function fetchDocuments(
  submissionId: string,
  fieldName: string
): Promise<DocumentMetadata[]> {
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('submission_id', submissionId)
      .eq('type', fieldName)
      .eq('is_active', true);

    if (error) {
      console.error('Error fetching documents:', error);
      return [];
    }
    return data as DocumentMetadata[];
  } catch (error) {
    console.error('Failed to fetch documents:', error);
    return [];
  }
}

/**
 * deleteDocument
 * --------------
 * Removes a single document from its bucket and from the `documents` table.
 * 
 * @param documentId The doc's primary key in DB
 * @param filePath   The storage path
 * @returns true if successful, otherwise an ErrorResponse
 */
export async function deleteDocument(
  documentId: string,
  filePath: string
): Promise<true | ErrorResponse> {
  logOperation('deleteDocument:start', { documentId, filePath });

  // We must figure out which bucket it's in (user vs system) from the path or a separate approach.
  // For simplicity, check if it starts with a known submissionId or do a second query to fetch the doc row:
  // We'll do a quick approach: If the doc row says user_id is null => system doc
  // If user_id is not null => user doc
  // Let's do that to be certain.
  let bucket: string = USER_BUCKET; // default assumption

  try {
    const { data: docData, error: docError } = await supabase
      .from('documents')
      .select('user_id')
      .eq('id', documentId)
      .single();

    if (docError || !docData) {
      // fallback if we can't find the doc
      logOperation('deleteDocument:db-fetch-doc-error', { documentId }, docError);
      return { message: 'Document record not found or DB error.' };
    }
    if (docData.user_id === null) {
      bucket = SYSTEM_GENERATED_BUCKET;
    }

    // Now remove from the correct bucket
    logOperation('deleteDocument:storage-remove', { filePath, bucket });

    const { error: storageError } = await supabase.storage
      .from(bucket)
      .remove([filePath]);

    if (storageError) {
      logOperation('deleteDocument:storage-remove-error', { filePath, bucket }, storageError);
      return { message: 'Failed to delete the file from storage.' };
    }

    logOperation('deleteDocument:storage-remove-success', { filePath, bucket });

    // Then remove row from DB
    logOperation('deleteDocument:db-delete', { documentId });
    const { error: dbError } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (dbError) {
      logOperation('deleteDocument:db-delete-error', { documentId }, dbError);
      return { message: 'Failed to remove document record.' };
    }

    logOperation('deleteDocument:success', { documentId, filePath });
    return true;
  } catch (error) {
    logOperation('deleteDocument:unexpected-error', { documentId, filePath }, error);
    return { message: 'An unexpected error occurred during deletion.' };
  }
}

/**
 * getDocumentUrl
 * --------------
 * Returns a public URL for the given file path. For user docs, 
 * we typically rely on RLS, but for system docs, we might want
 * a non-public approach. This function shows a simplified usage.
 * 
 * @param filePath path in the appropriate bucket
 * @param isSystemDoc whether it belongs to the system doc bucket
 */
export function getDocumentUrl(filePath: string, isSystemDoc: boolean = false): string {
  const bucket = isSystemDoc ? SYSTEM_GENERATED_BUCKET : USER_BUCKET;
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}
